import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, TextField, MenuItem, Button, Paper, Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { PageHeader } from '../../components/ui/PageHeader';
import { DataTable } from '../../components/common/DataTable';
import { gpsAPI, vehicleAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { getPageSubtitle } from '../../constants/pageSubtitles';

export const GPSReportsPage: React.FC = () => {
  const { showToast } = useToast();
  const [reportTypes, setReportTypes] = useState<Array<{ key: string; label: string }>>([]);
  const [reportType, setReportType] = useState('daily_movement');
  const [vehicleId, setVehicleId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [columns, setColumns] = useState<Array<{ field: string; header: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState<Array<{ _id: string; vehicleNumber: string }>>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    gpsAPI.getReportTypes().then((r) => setReportTypes(r.data || [])).catch(() => {});
    vehicleAPI.getAll().then((r) => setVehicles(r.data || [])).catch(() => {});
  }, []);

  const runReport = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (vehicleId) params.vehicleId = vehicleId;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await gpsAPI.runReport(reportType, params);
      const data = res.data || [];
      setRows(data);
      if (data.length > 0) {
        const keys = Object.keys(data[0]).filter((k) => !k.startsWith('_'));
        setColumns(keys.map((k) => ({ field: k, header: k.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()) })));
      } else {
        setColumns([]);
      }
    } catch {
      showToast('Report failed', 'error');
    } finally {
      setLoading(false);
    }
  }, [reportType, vehicleId, startDate, endDate, showToast]);

  return (
    <Box>
      <PageHeader
        title="GPS Reports"
        subtitle={getPageSubtitle('gps_reports')}
        icon={<AssessmentIcon />}
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <TextField select size="small" label="Report Type" value={reportType} onChange={(e) => setReportType(e.target.value)} sx={{ minWidth: 220 }}>
            {reportTypes.map((r) => <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Vehicle" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Vehicles</MenuItem>
            {vehicles.map((v) => <MenuItem key={v._id} value={v._id}>{v.vehicleNumber}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Start Date" type="date" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <TextField size="small" label="End Date" type="date" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          <Button variant="contained" onClick={runReport} disabled={loading}>
            {loading ? 'Running...' : 'Run Report'}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Available: daily movement, monthly travel, distance, speed violations, stop/idle, location history
        </Typography>
      </Paper>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        search={search}
        onSearchChange={setSearch}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        getRowId={(row) => String(row._id || row.vehicleId || row.date || row.month || Math.random())}
        selectable={false}
        emptyMessage="Select a report type and click Run Report."
      />
    </Box>
  );
};
